import type { JSX } from 'react'
import { Pencil, RadioTower, Trash2, TriangleAlert } from 'lucide-react'
import type { ServerDto, ServerProbeDto } from '@/lib/dto'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { OnTop, Surface } from '@/ui/primitives/Surface'
import { ProbeBadge } from './ProbeBadge'

/**
 * Карточка профиля сервера.
 *
 * Порядок сведений — по частоте вопросов о сервере: «отвечает ли», «та ли
 * версия», «что бот делает после входа», «сколько на нём хуков». Адрес набран
 * монопространственным: его сверяют символ в символ.
 *
 * Клик по карточке открывает правку — у профиля нет отдельной страницы, и
 * прицеливаться в карандаш незачем.
 */

export interface ServerTileProps {
  server: ServerDto
  canOperate: boolean
  pinging?: boolean
  /**
   * Проба, только что полученная кнопкой «Пропинговать». Приоритетнее пробы из
   * списка: в списке `latencyMs` всегда `null` (в профиле нет колонки под
   * задержку), а свежий ответ её содержит.
   */
  freshProbe?: ServerProbeDto | null
  /** Почему последний пинг не удался. Показывается рядом с кнопкой. */
  pingError?: string | null
  onPing: () => void
  onEdit: () => void
  onDelete: () => void
}

export function ServerTile({
  server,
  canOperate,
  pinging = false,
  freshProbe = null,
  pingError = null,
  onPing,
  onEdit,
  onDelete
}: ServerTileProps): JSX.Element {
  const hooksOn = server.hooks.filter(hook => hook.enabled).length
  const probe = freshProbe ?? server.probe
  const motd = probe?.motd ?? null
  const noRights = canOperate ? undefined : 'Недостаточно прав'

  return (
    <Surface
      variant="raised"
      onActivate={onEdit}
      // Имя растянутой кнопки НЕ совпадает с именем карандаша: два элемента с
      // одинаковой подписью в одной карточке читаются скринридером как дубль.
      activateLabel={`Открыть профиль ${server.name}`}
      className="flex h-full flex-col"
      data-server-id={server.id}
    >
      <div className="flex min-w-0 flex-col gap-1 px-3.5 pt-3">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <p className="truncate font-display text-md font-semibold text-ink">{server.name}</p>
          <Chip mono title="Версия из профиля">
            {server.versionMode === 'manual' ? (server.version ?? '—') : 'авто'}
          </Chip>
        </div>
        <p className="truncate font-mono text-xs text-ink-muted">
          {server.host}:{server.port}
        </p>
      </div>

      <div className="mt-3 px-3.5">
        <ProbeBadge probe={probe} />
      </div>

      {motd === null ? null : (
        <p className="mt-2 truncate px-3.5 font-mono text-2xs text-ink-muted" title={motd}>
          {motd}
        </p>
      )}

      <dl className="mt-3 grid grid-cols-1 gap-1 px-3.5 text-2xs">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="shrink-0 text-ink-muted">После входа</dt>
          <dd className="truncate font-mono text-ink">
            {server.joinCommand ?? 'ничего не делать'}
          </dd>
        </div>
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="shrink-0 text-ink-muted">Хуки</dt>
          <dd className="truncate text-ink">
            {server.hooks.length === 0 ? 'нет' : `${server.hooks.length}, включено ${hooksOn}`}
          </dd>
        </div>
      </dl>

      <OnTop className="mt-auto border-t border-line px-2 py-1.5">
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="secondary"
            loading={pinging}
            disabled={!canOperate}
            title={noRights ?? 'Спросить сервер о версии и задержке'}
            onClick={onPing}
          >
            <RadioTower aria-hidden className="size-3.5" />
            Пропинговать
          </Button>

          <IconButton
            label={`Изменить профиль ${server.name}`}
            size="sm"
            className="ml-auto"
            disabled={!canOperate}
            onClick={onEdit}
          >
            <Pencil aria-hidden className="size-4" />
          </IconButton>
          <IconButton
            label={`Удалить профиль ${server.name}`}
            size="sm"
            tone="danger"
            disabled={!canOperate}
            onClick={onDelete}
          >
            <Trash2 aria-hidden className="size-4" />
          </IconButton>
        </div>

        {/* Ошибка пинга живёт здесь, а не в тосте: тост уезжает, а разбираться с
            причиной приходится глядя ровно на эту кнопку. */}
        {pingError === null ? null : (
          <p
            role="alert"
            className="mt-1.5 flex items-start gap-1.5 rounded-sm bg-fail/12 px-2 py-1.5 text-2xs text-fail"
          >
            <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
            <span>{pingError}</span>
          </p>
        )}
      </OnTop>
    </Surface>
  )
}
